import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/verticals";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Users, Storefront, Receipt, CurrencyEur, ChartBar } from "@phosphor-icons/react";

const AdminDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    if (!loading && !user) { navigate("/login"); return; }
    if (!loading && user && user.role !== "admin") { navigate("/dashboard"); return; }
    if (user && user.role === "admin") {
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
      api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
      api.get("/admin/providers").then((r) => setProviders(r.data)).catch(() => {});
    }
  }, [user, loading, navigate]);

  if (!user || user.role !== "admin") return null;

  const cards = [
    { label: "Utenti", value: stats?.users || 0, Icon: Users, cls: "text-primary" },
    { label: "Professionisti", value: stats?.providers || 0, Icon: Storefront, cls: "text-blue-600" },
    { label: "Prenotazioni", value: stats?.bookings || 0, Icon: Receipt, cls: "text-amber-600" },
    { label: "GMV", value: eur(stats?.gmv || 0), Icon: ChartBar, cls: "text-emerald-600" },
    { label: "Commissioni", value: eur(stats?.commission || 0), Icon: CurrencyEur, cls: "text-emerald-600" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold">Pannello Admin</h1>
      <p className="text-muted-foreground">Controllo piattaforma NEXA</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6" data-testid="admin-stats">
        {cards.map((c) => (
          <div key={c.label} className="bg-card border border-border rounded-2xl p-4">
            <c.Icon size={20} className={c.cls} weight="duotone" />
            <div className="text-xl font-bold font-heading mt-2">{c.value}</div>
            <div className="text-xs text-muted-foreground">{c.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="bookings" className="mt-8">
        <TabsList>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Prenotazioni</TabsTrigger>
          <TabsTrigger value="aziende" data-testid="admin-tab-aziende">Aziende</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Utenti</TabsTrigger>
        </TabsList>
        <TabsContent value="bookings" className="mt-4">
          <div className="border border-border rounded-2xl overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Servizio</TableHead><TableHead>Cliente</TableHead><TableHead>Pro</TableHead><TableHead>Data</TableHead><TableHead>Prezzo</TableHead><TableHead>Commissione</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
              <TableBody>
                {bookings.map((b) => (
                  <TableRow key={b.booking_id}>
                    <TableCell className="font-medium">{b.service_name}</TableCell>
                    <TableCell>{b.customer_name}</TableCell>
                    <TableCell>{b.provider_name}</TableCell>
                    <TableCell>{b.date}</TableCell>
                    <TableCell>{eur(b.price)}</TableCell>
                    <TableCell className="text-emerald-600">{eur(b.platform_fee)}</TableCell>
                    <TableCell><Badge variant={b.payment_status === "paid" ? "default" : "secondary"}>{b.payment_status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="aziende" className="mt-4">
          <div className="border border-border rounded-2xl overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Azienda</TableHead><TableHead>Categoria</TableHead><TableHead>Città</TableHead><TableHead>Prenotazioni</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {providers.map((p) => (
                  <TableRow key={p.provider_id} data-testid={`admin-provider-${p.provider_id}`}>
                    <TableCell className="font-medium">{p.business_name}</TableCell>
                    <TableCell><Badge variant="secondary">{p.vertical}</Badge></TableCell>
                    <TableCell>{p.city}</TableCell>
                    <TableCell>{p.bookings_count}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="rounded-full" data-testid={`open-provider-${p.provider_id}`} onClick={() => navigate(`/admin/provider/${p.provider_id}`)}>Apri</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="border border-border rounded-2xl overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell><Badge variant="secondary">{u.role}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
